import React, { useState } from 'react'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import {
  faFacebookSquare,
  faTwitterSquare,
} from '@fortawesome/free-brands-svg-icons'
import { faCopy, faEnvelope } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Space, Tooltip } from 'antd'
import type { PublicVideoRequest } from 'apollo-gql'
import cn from 'classnames'
import { useTranslation } from 'lib/i18n'
import config from 'lib/nextConfig'
import trackVideoRequestDownloaded from 'lib/tracking/avo/trackVideoRequestDownloaded'
import trackVideoRequestShared from 'lib/tracking/avo/trackVideoRequestShared'
import Image from 'next/image'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { isMobile } from 'react-device-detect'
import { FacebookProvider, Share } from 'react-facebook'

import useSharingButtons from './useSharingButtons'

import styles from './SharingButtons.module.css'

type SharingIconProps = {
  request?: PublicVideoRequest
  url: string
  icon: string
  name: string
  download?: {
    from: string
    to: string
    url?: string | undefined
  }
}

const SharingIcon = ({
  request,
  url,
  icon,
  name,
  download,
}: SharingIconProps) => {
  const [copied, setCopied] = useState(false)
  const { t } = useTranslation()
  const textCopied = t(
    'SharingButtons.textCopied',
    'Link copied - just paste it to share it',
  )
  const isSocialIcon = !['download', 'copy', 'email', 'sms'].includes(name)
  const downloadUrl = download?.url

  const copyLink = () => {
    setCopied(true)
    return setTimeout(() => setCopied(false), 1500)
  }

  if (name === 'copy') {
    return (
      <CopyToClipboard text={url} onCopy={copyLink}>
        <Tooltip title={textCopied} trigger="click" visible={copied}>
          <button type="button" className={cn(styles.btnCopy, styles.btnIcon)}>
            <Image src={icon} alt={name} width={42} height={42} />
          </button>
        </Tooltip>
      </CopyToClipboard>
    )
  }

  const onClick = () => {
    if (request && !downloadUrl) {
      trackVideoRequestShared({ request })
    }

    if (request && downloadUrl) {
      trackVideoRequestDownloaded({ request })
    }
  }

  return (
    <a
      className={styles.btnIcon}
      href={url}
      target={isSocialIcon && isMobile ? '_blank' : undefined}
      download
      rel="noopener noreferrer"
      onClick={onClick}
    >
      <Image src={icon} alt={name} width={42} height={42} />
    </a>
  )
}

type SharingButtonProps = {
  text: string
  icon: IconProp
}

const SharingButton = ({ text, icon, ...props }: SharingButtonProps) => (
  <button className={styles.btn} type="button" {...props}>
    <FontAwesomeIcon icon={icon} />
    <span className={styles.sharingButtonText}>{text}</span>
  </button>
)

type CopyToClipboardIconProps = {
  url: string
  t: (arg0: string, arg1: string) => string
}

const CopyToClipboardIcon = ({ url, t }: CopyToClipboardIconProps) => {
  const [copied, setCopied] = useState(false)

  return (
    <CopyToClipboard
      text={url}
      onCopy={() => {
        setCopied(true)
      }}
    >
      <SharingButton
        icon={faCopy}
        text={
          copied
            ? t('CopyToClipboardIcon.copied', 'Copied!')
            : t('CopyToClipboardIcon.copy', 'Copy!')
        }
        data-track="share-link-copied"
      />
    </CopyToClipboard>
  )
}

type EmailLinkProps = {
  subject: string
  body: string
  text: string
}

const EmailLink = ({ subject, body, text }: EmailLinkProps) => (
  <a href={`mailto:?to=&body=${body}&subject=${subject}`}>
    <SharingButton icon={faEnvelope} text={text} data-track="share-email" />
  </a>
)

type FacebookShareProps = {
  url: string
  text: string
}

const FacebookShare = ({ url, text }: FacebookShareProps) => {
  const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
    url,
  )}`

  return (
    <FacebookProvider appId="929583024085600">
      <Share href={url}>
        {({ handleClick, loading }) => (
          <a
            onClick={(e) => {
              if (e) {
                e.preventDefault()
              }
              if (loading) return false
              handleClick(e)

              return undefined
            }}
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={text}
            id="facebook-share-link"
          >
            <SharingButton
              icon={faFacebookSquare}
              text="Facebook"
              data-track="share-facebook"
            />
          </a>
        )}
      </Share>
    </FacebookProvider>
  )
}

type TwitterShareProps = {
  url: string
  text: string
}

const TwitterShare = ({ url, text }: TwitterShareProps) => {
  // Separate your hashtags with commas. Do not add the hash character (#) or spaces.
  const hashtags = 'memmo'

  const twitterIntentUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
    url,
  )}&hastags=${escape(hashtags)}&hashtags=${encodeURIComponent(
    hashtags,
  )}&text=${escape(text)}`

  return (
    <a href={twitterIntentUrl} target="_blank" rel="noopener noreferrer">
      <SharingButton
        icon={faTwitterSquare}
        text="Twitter"
        data-track="share-twitter"
      />
    </a>
  )
}

type SharingButtonsProps = {
  request?: PublicVideoRequest
  url?: string
  relativeUrl: string
  showIcons: boolean
  download?: {
    url?: string | undefined
    to: string
    from: string
  }
  talentSlug: string
  hideSocialMediaLinks?: boolean
  sharingText?: {
    twitterText?: string
    emailSubject?: string
    smsSubject?: string
    emailText?: string
    facebookText?: string
  }
  hideInstagram?: boolean
  hideFacebook?: boolean
}

const SharingButtons: React.FC<SharingButtonsProps> = ({
  request,
  url: absoluteUrl,
  relativeUrl,
  showIcons = false,
  download,
  talentSlug,
  hideSocialMediaLinks = false,
  sharingText = {},
  hideInstagram,
  hideFacebook,
}) => {
  const { t } = useTranslation()
  const baseUrl = config.WEBAPP_URL
  const url = absoluteUrl || `${baseUrl}${relativeUrl}`
  const twitterText =
    sharingText.twitterText ||
    t('SharingButtons.twitterText', 'Check out this memmo!')
  const emailSubject =
    sharingText.emailSubject ||
    t('SharingButtons.emailSubject', 'Check out this memmo!')
  const smsSubject = isMobile
    ? sharingText.smsSubject ||
      t('SharingButtons.smsSubject', 'Check out this memmo!')
    : undefined
  const emailText =
    sharingText.emailText || t('SharingButtons.email', 'Send email')
  const facebookText =
    sharingText.facebookText ||
    t('SharingButtons.facebookText', 'Share {{url}} on Facebook', {
      url,
    })
  const subtitles = {
    email: t('SharingButtons.emailSubtitle', 'Email'),
    sms: t('SharingButtons.smsSubtitle', 'Sms'),
    copy: t('SharingButtons.copySubtitle', 'Copy link'),
    download: t('SharingButtons.downloadSubtitle', 'Download'),
  }
  const shareButtons = useSharingButtons({
    url,
    emailSubject,
    smsSubject,
    showCopy: true,
    subtitles,
    download,
    talentSlug,
  })
  const socialButtons = useSharingButtons({
    url,
    twitterText,
    showFacebook: !hideFacebook,
    showInstagram: !hideInstagram,
    talentSlug,
  })

  if (showIcons) {
    return (
      <div className={styles.share}>
        <div
          className={styles.shareButtons}
          data-cy="videoRequestPageShareButtons"
        >
          {shareButtons.map((button) => (
            <div
              className={styles.shareButtonsItem}
              key={`social-icon-${button.name}`}
            >
              <SharingIcon key={button.name} {...button} request={request} />
              <span className={styles.shareButtonsItemSubtitle}>
                {button.subtitle}
              </span>
            </div>
          ))}
        </div>

        {!hideSocialMediaLinks && (
          <div
            className={styles.socialButtons}
            data-cy="videoRequestPageSocialButtons"
          >
            {socialButtons.map((button) => (
              <SharingIcon key={button.name} {...button} request={request} />
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <Space size={20}>
      <FacebookShare url={url} text={facebookText} />
      <TwitterShare url={url} text={twitterText} />
      <EmailLink subject={emailSubject} body={url} text={emailText} />
      <CopyToClipboardIcon url={url} t={t} />
    </Space>
  )
}

export default SharingButtons
